/**
 * One dated item in Home's Scheduled tab: a review, an effective date or a
 * comment deadline, with "Prepare with Amoreg" on hover and its day.
 *
 * @module components/home/HomeScheduledRow
 */
import { Link } from "@tanstack/react-router";
import { ClipboardCheckIcon, LandmarkIcon, MessageSquareTextIcon, SparklesIcon } from "lucide-react";
import type { ComponentType } from "react";

import { publicationHash } from "../regulation/monitor/publicationLink";
import { ListRow, ListRowDate, ListRowIcon, ListRowTitle } from "../ui/list-row";
import { Tip } from "../ui/tip";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { preparePrompt } from "./HomeAgenda.logic";
import { dayMonth } from "./homeDates.logic";
import { comingUpName, type HomeComingUpItem } from "./homeLogic";
import { scheduledRowTooltip } from "./homeTabs.logic";

const KIND_ICON: Readonly<Record<HomeComingUpItem["kind"], ComponentType<{ className?: string }>>> = {
  review: ClipboardCheckIcon,
  effective: LandmarkIcon,
  comments: MessageSquareTextIcon,
};

export function HomeScheduledRow({
  item,
  orgSegment,
  now,
  onPrepare,
}: {
  readonly item: HomeComingUpItem;
  readonly orgSegment: string;
  readonly now: number;
  readonly onPrepare: (prompt: string) => void;
}) {
  const Icon = KIND_ICON[item.kind];
  return (
    <li>
      <Tip label={scheduledRowTooltip(item)}>
        <ListRow
          render={
            item.policyId ? (
              <Link to="/$org/compliance/policies/$policyId" params={{ org: orgSegment, policyId: item.policyId }} />
            ) : (
              <Link
                to="/$org/compliance/regulatory"
                params={{ org: orgSegment }}
                {...(item.updateId ? { hash: publicationHash(item.updateId) } : {})}
              />
            )
          }
        >
          <ListRowIcon>
            <Icon />
          </ListRowIcon>
          <ListRowTitle ink="soft">{comingUpName(item)}</ListRowTitle>
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label="Prepare with Amoreg"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onPrepare(preparePrompt(item));
                  }}
                  className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground focus-visible:opacity-100 group-hover/list-row:opacity-100 [@media(hover:none)]:opacity-100"
                />
              }
            >
              <SparklesIcon className="size-3.5 stroke-[1.75]" />
            </TooltipTrigger>
            <TooltipPopup side="top">Prepare with Amoreg</TooltipPopup>
          </Tooltip>
          <ListRowDate urgent={item.overdue}>{dayMonth(Date.parse(item.at), now)}</ListRowDate>
        </ListRow>
      </Tip>
    </li>
  );
}
